/**
 * {@link EventLifecycleControls} — displays the current event status badge and
 * transition buttons for valid lifecycle transitions.
 *
 * Valid transitions (Req 11.7):
 *   draft → published
 *   published → active
 *   active → complete (triggers close-scoring review)
 *   complete → active (reopen)
 *   complete → closed
 *
 * Requirements: 11.7
 */

import { useState } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

export type EventStatus = 'draft' | 'published' | 'active' | 'complete' | 'closed';

export interface EventLifecycleControlsProps {
  eventId: string;
  status: EventStatus;
  apiClient: ApiClient;
  /** Called after a successful status transition so the parent can refresh. */
  onStatusChange: (newStatus: EventStatus) => void;
  /** Called when the "Complete" transition is initiated (active → complete) to show the review screen. */
  onInitiateClose: () => void;
}

interface TransitionDef {
  from: EventStatus;
  to: EventStatus;
  label: string;
  variant: 'primary' | 'secondary';
}

const TRANSITIONS: TransitionDef[] = [
  { from: 'draft', to: 'published', label: 'Publish', variant: 'primary' },
  { from: 'published', to: 'active', label: 'Activate', variant: 'primary' },
  { from: 'active', to: 'complete', label: 'Complete', variant: 'primary' },
  { from: 'complete', to: 'active', label: 'Reopen', variant: 'secondary' },
  { from: 'complete', to: 'closed', label: 'Close', variant: 'primary' },
];

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
  marginBottom: '1rem',
};

const statusBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '0.25rem 0.75rem',
  borderRadius: '1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
};

function getStatusColor(status: EventStatus): CSSProperties {
  switch (status) {
    case 'draft':
      return { background: '#fff8c5', color: '#6a5300' };
    case 'published':
      return { background: '#e6f4ea', color: 'var(--color-brand-green, #1B4332)' };
    case 'active':
      return { background: '#dafbe1', color: 'var(--color-brand-green, #1B4332)' };
    case 'complete':
      return { background: 'var(--color-bg-page, #FAFBFC)', color: 'var(--color-text-secondary, #6B7280)' };
    case 'closed':
      return { background: '#ffebe9', color: 'var(--color-brand-red, #DC2F02)' };
    default:
      return { background: 'var(--color-bg-page, #FAFBFC)', color: 'var(--color-text-secondary, #6B7280)' };
  }
}

export function EventLifecycleControls({
  eventId,
  status,
  apiClient,
  onStatusChange,
  onInitiateClose,
}: EventLifecycleControlsProps): JSX.Element {
  const [transitioning, setTransitioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableTransitions = TRANSITIONS.filter((t) => t.from === status);

  async function handleTransition(target: EventStatus): Promise<void> {
    // The active → complete transition requires the close-scoring review
    if (status === 'active' && target === 'complete') {
      onInitiateClose();
      return;
    }

    setTransitioning(true);
    setError(null);
    try {
      await apiClient.request<unknown>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/status`,
        { body: { status: target } },
      );
      onStatusChange(target);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to transition event status.');
    } finally {
      setTransitioning(false);
    }
  }

  return (
    <div aria-label="Event lifecycle controls">
      {error && (
        <ErrorBanner title="Transition Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}
      <div style={containerStyle}>
        <span
          style={{ ...statusBadgeStyle, ...getStatusColor(status) }}
          aria-label={`Event status: ${status}`}
        >
          {status}
        </span>

        {availableTransitions.map((t) => (
          <Button
            key={t.to}
            variant={t.variant}
            onClick={() => { void handleTransition(t.to); }}
            disabled={transitioning}
            aria-label={`${t.label} event`}
          >
            {transitioning ? <Spinner size="1rem" label="Transitioning…" /> : t.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
